/**
 * DashboardPage.js — Dashboard principal CORVIAN
 * Pulls live data from existing endpoints, no new backend needed.
 *
 * Layout:
 *   ┌─ Hero greeting ──────────────────────────────────────┐
 *   ├─ KPI row (4 cards) ──────────────────────────────────┤
 *   ├─ Left col (2/3)          │ Right col (1/3)           │
 *   │  ├─ Flujo bancario       │  Actividad reciente       │
 *   │  └─ CxC vs CxP           │  Accesos rápidos          │
 *   └──────────────────────────┴───────────────────────────┘
 */
import { useState, useEffect } from "react";
import { Link }                from "react-router-dom";
import apiFetch                from "../../../shared/services/apiClient";
import { formatCurrency }      from "../../../shared/utils/formatCurrency";

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmt  = (n) => `₡${formatCurrency(Number(n ?? 0))}`;
const fmtK = (n) => {
  const v = Number(n ?? 0);
  if (v >= 1_000_000) return `₡${(v/1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `₡${(v/1_000).toFixed(0)}K`;
  return `₡${v.toFixed(0)}`;
};

function fmtDate(iso) {
  if (!iso) return "—";
  const [y,m,d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

function useAsync(fn, deps = []) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    fn().then(setData).catch(() => setData(null)).finally(() => setLoading(false));
    // eslint-disable-next-line
  }, deps);
  return { data, loading };
}

// ── Sub-components ────────────────────────────────────────────────────────────

function KpiCard({ icon, label, value, sub, color, loading, to }) {
  const card = (
    <div style={{
      background: "#1f2937",
      border: "1px solid #364250",
      borderRadius: 12,
      padding: "20px 22px",
      display: "flex",
      flexDirection: "column",
      gap: 10,
      position: "relative",
      overflow: "hidden",
      transition: "border-color 0.2s, transform 0.15s",
      cursor: to ? "pointer" : "default",
      textDecoration: "none",
    }}
    onMouseOver={e => { if(to){ e.currentTarget.style.borderColor=color; e.currentTarget.style.transform="translateY(-2px)"; }}}
    onMouseOut={e => { e.currentTarget.style.borderColor="#364250"; e.currentTarget.style.transform="translateY(0)"; }}>
      {/* Glow */}
      <div style={{ position:"absolute", top:-30, right:-30, width:120, height:120,
        borderRadius:"50%", background:`${color}18`, pointerEvents:"none" }}/>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
        <div style={{ background:`${color}18`, border:`1px solid ${color}30`,
          borderRadius:10, width:42, height:42, display:"flex",
          alignItems:"center", justifyContent:"center" }}>
          <i className={`fa-solid ${icon}`} style={{ color, fontSize:"1rem" }}/>
        </div>
        {loading && (
          <i className="fa-solid fa-circle-notch fa-spin"
             style={{ color:"#4b5563", fontSize:"0.8rem" }}/>
        )}
      </div>
      <div>
        <div style={{ fontSize:"1.55rem", fontWeight:800, color:"#f9fafb",
          fontFamily:"'Montserrat',sans-serif", letterSpacing:"-0.5px", lineHeight:1 }}>
          {loading ? <span style={{ color:"#374151" }}>——</span> : value}
        </div>
        <div style={{ fontSize:"0.78rem", color:"#6b7280", marginTop:4, fontWeight:500 }}>
          {label}
        </div>
        {sub && !loading && (
          <div style={{ fontSize:"0.72rem", color:"#4b5563", marginTop:2 }}>{sub}</div>
        )}
      </div>
    </div>
  );
  return to ? <Link to={to} style={{ textDecoration:"none" }}>{card}</Link> : card;
}

function SectionTitle({ icon, label, color = "#f97316" }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:14 }}>
      <i className={`fa-solid ${icon}`} style={{ color, fontSize:"0.9rem" }}/>
      <span style={{ fontSize:"0.82rem", fontWeight:700, letterSpacing:"0.8px",
        textTransform:"uppercase", color:"#8d98a7" }}>
        {label}
      </span>
    </div>
  );
}

function Panel({ children, style = {} }) {
  return (
    <div style={{ background:"#1f2937", border:"1px solid #364250",
      borderRadius:12, padding:"20px 22px", ...style }}>
      {children}
    </div>
  );
}

function MiniRow({ icon, label, value, color, sub }) {
  return (
    <div style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 0",
      borderBottom:"1px solid #1e293b" }}>
      <div style={{ width:34, height:34, borderRadius:8, flexShrink:0,
        background:`${color}15`, display:"flex", alignItems:"center", justifyContent:"center" }}>
        <i className={`fa-solid ${icon}`} style={{ color, fontSize:"0.82rem" }}/>
      </div>
      <div style={{ flex:1, minWidth:0 }}>
        <div style={{ fontSize:"0.83rem", color:"#e5ecf5", fontWeight:500,
          overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{label}</div>
        {sub && <div style={{ fontSize:"0.72rem", color:"#6b7280", marginTop:1 }}>{sub}</div>}
      </div>
      <div style={{ fontFamily:"monospace", fontWeight:700, fontSize:"0.83rem",
        color, flexShrink:0, textAlign:"right" }}>{value}</div>
    </div>
  );
}

function QuickLink({ to, icon, label, color }) {
  return (
    <Link to={to} style={{ textDecoration:"none" }}>
      <div style={{ display:"flex", alignItems:"center", gap:10,
        padding:"10px 14px", background:"rgba(255,255,255,0.02)",
        border:"1px solid #2d3748", borderRadius:8, cursor:"pointer",
        transition:"all 0.15s" }}
        onMouseOver={e => { e.currentTarget.style.background=`${color}10`; e.currentTarget.style.borderColor=`${color}40`; }}
        onMouseOut={e => { e.currentTarget.style.background="rgba(255,255,255,0.02)"; e.currentTarget.style.borderColor="#2d3748"; }}>
        <i className={`fa-solid ${icon}`} style={{ color, fontSize:"0.85rem", width:16 }}/>
        <span style={{ fontSize:"0.82rem", color:"#9ca3af", fontWeight:500 }}>{label}</span>
        <i className="fa-solid fa-arrow-right" style={{ color:"#4b5563", fontSize:"0.65rem", marginLeft:"auto" }}/>
      </div>
    </Link>
  );
}

// Bar for CxC vs CxP comparison
function BarCompare({ labelA, valA, labelB, valB, colorA, colorB }) {
  const total = valA + valB || 1;
  const pctA  = (valA / total) * 100;
  const pctB  = (valB / total) * 100;
  return (
    <div style={{ marginTop:4 }}>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6, fontSize:"0.8rem" }}>
        <span style={{ color:colorA, fontWeight:600 }}>{labelA}</span>
        <span style={{ color:colorB, fontWeight:600 }}>{labelB}</span>
      </div>
      <div style={{ height:8, borderRadius:4, background:"#374151",
        display:"flex", overflow:"hidden" }}>
        <div style={{ width:`${pctA}%`, background:colorA,
          borderRadius:"4px 0 0 4px", transition:"width 0.8s ease" }}/>
        <div style={{ width:`${pctB}%`, background:colorB,
          borderRadius:"0 4px 4px 0", transition:"width 0.8s ease" }}/>
      </div>
      <div style={{ display:"flex", justifyContent:"space-between", marginTop:5, fontSize:"0.75rem" }}>
        <span style={{ color:"#6b7280" }}>{fmtK(valA)}</span>
        <span style={{ color:"#6b7280" }}>{fmtK(valB)}</span>
      </div>
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────────────────────────────────
export default function DashboardPage() {

  // ── Fetch all data ──────────────────────────────────────────────────────────
  const { data: filiales } = useAsync(() =>
    apiFetch("/api/residents/filial/?page_size=200").then(r=>r.json())
  );

  const { data: propietarios } = useAsync(() =>
    apiFetch("/api/residents/propietario/?page_size=1").then(r=>r.json())
  );

  const { data: facturas } = useAsync(() =>
    apiFetch("/api/finance/factura/?page_size=5&ordering=-fecha_emision").then(r=>r.json())
  );

  const { data: cxcData, loading: cxcLoad } = useAsync(() =>
    apiFetch("/api/finance/cuentaXCobrar/?page_size=200").then(r=>r.json())
  );

  const { data: cxpData, loading: cxpLoad } = useAsync(() =>
    apiFetch("/api/finance/cuentaXPagar/?page_size=200").then(r=>r.json())
  );

  const { data: bancos, loading: bancosLoad } = useAsync(() =>
    apiFetch("/api/finance/cuentaBancaria/?page_size=20").then(r=>r.json())
  );

  const { data: asientos } = useAsync(() =>
    apiFetch("/api/finance/asientos/?page_size=5&ordering=-fecha").then(r=>r.json())
  );

  const { data: recibos } = useAsync(() =>
    apiFetch("/api/finance/recibo/?page_size=5&ordering=-fecha_pago").then(r=>r.json())
  );

  const { data: condominio } = useAsync(() =>
    apiFetch("/api/residents/condominio/").then(r=>r.json())
      .then(d => d.results?.[0] ?? d)
  );

  // ── Derived values ──────────────────────────────────────────────────────────
  const cxcList    = cxcData?.results ?? cxcData ?? [];
  const cxpList    = cxpData?.results ?? cxpData ?? [];
  const bancosList = bancos?.results  ?? bancos  ?? [];
  const factList   = facturas?.results ?? [];
  const asienList  = asientos?.results ?? [];
  const reciList   = recibos?.results  ?? [];

  const totalCxC    = cxcList.reduce((s,x) => s + Number(x.saldo ?? 0), 0);
  const totalCxP    = cxpList.reduce((s,x) => s + Number(x.saldo ?? 0), 0);
  const totalBanco  = bancosList.reduce((s,b) => s + Number(b.saldo ?? 0), 0);
  const nFiliales   = filiales?.count ?? filiales?.results?.length ?? 0;
  const nPropiet    = propietarios?.count ?? 0;
  const nPendCxC    = cxcList.filter(x => Number(x.saldo) > 0).length;

  // Today greeting
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Buenos días" : hour < 19 ? "Buenas tardes" : "Buenas noches";
  const today = new Date().toLocaleDateString("es-CR", {
    weekday:"long", year:"numeric", month:"long", day:"numeric"
  });

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>

      {/* ── Hero greeting ── */}
      <div style={{ background:"linear-gradient(135deg, #1f2937 0%, #111827 100%)",
        border:"1px solid #364250", borderRadius:12, padding:"24px 28px",
        display:"flex", justifyContent:"space-between", alignItems:"center",
        flexWrap:"wrap", gap:12, position:"relative", overflow:"hidden" }}>
        {/* dot pattern */}
        <div style={{ position:"absolute", inset:0, pointerEvents:"none",
          backgroundImage:"radial-gradient(circle, rgba(249,115,22,0.08) 1px, transparent 1px)",
          backgroundSize:"28px 28px",
          maskImage:"radial-gradient(ellipse 60% 100% at 100% 50%, black 0%, transparent 100%)",
          WebkitMaskImage:"radial-gradient(ellipse 60% 100% at 100% 50%, black 0%, transparent 100%)" }}/>
        <div>
          <p style={{ margin:0, fontSize:"0.8rem", color:"#6b7280",
            fontWeight:500, textTransform:"capitalize" }}>{today}</p>
          <h1 style={{ margin:"4px 0 0", fontSize:"1.5rem", fontWeight:800,
            fontFamily:"'Montserrat',sans-serif", color:"#f9fafb", letterSpacing:"-0.3px" }}>
            {greeting},{" "}
            <span style={{ color:"#f97316" }}>
              {condominio?.nombre ?? "Condominio"}
            </span>
          </h1>
          <p style={{ margin:"6px 0 0", fontSize:"0.83rem", color:"#6b7280" }}>
            Aquí está el resumen financiero de hoy
          </p>
        </div>
        <div style={{ display:"flex", gap:8 }}>
          <Link to="/finanzas/facturas/agregar">
            <button style={{ background:"#f97316", border:"none", borderRadius:8,
              padding:"9px 16px", color:"#fff", fontWeight:700, fontSize:"0.8rem",
              cursor:"pointer", display:"flex", alignItems:"center", gap:7 }}>
              <i className="fa-solid fa-plus"/>Nueva Factura
            </button>
          </Link>
          <Link to="/finanzas/cxp/facturas/agregar">
            <button style={{ background:"transparent", border:"1px solid #364250",
              borderRadius:8, padding:"9px 16px", color:"#9ca3af", fontWeight:600,
              fontSize:"0.8rem", cursor:"pointer", display:"flex", alignItems:"center", gap:7 }}>
              <i className="fa-solid fa-file-invoice-dollar"/>Nueva CxP
            </button>
          </Link>
        </div>
      </div>

      {/* ── KPI cards ── */}
      <div style={{ display:"grid",
        gridTemplateColumns:"repeat(auto-fit, minmax(180px,1fr))", gap:12 }}>
        <KpiCard icon="fa-building" label="Filiales registradas"
          value={nFiliales} sub={`${nPropiet} propietario(s)`}
          color="#f97316" to="/residentes"/>
        <KpiCard icon="fa-file-invoice" label="Cuentas por Cobrar"
          value={fmtK(totalCxC)}
          sub={`${nPendCxC} filial(es) con saldo pendiente`}
          color="#f87171" to="/finanzas/cxc"/>
        <KpiCard icon="fa-file-invoice-dollar" label="Cuentas por Pagar"
          value={fmtK(totalCxP)}
          sub={`${cxpList.filter(x=>Number(x.saldo)>0).length} factura(s) pendiente(s)`}
          color="#fb923c" to="/finanzas/cxp"/>
        <KpiCard icon="fa-landmark" label="Saldo bancario total"
          value={fmtK(totalBanco)}
          sub={`${bancosList.length} cuenta(s) activa(s)`}
          color="#4ade80" to="/finanzas/asientos"/>
      </div>

      {/* ── Body ── */}
      <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr", gap:16 }}>

        {/* ── LEFT column ── */}
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

          {/* Cuentas bancarias */}
          <Panel>
            <SectionTitle icon="fa-landmark" label="Saldo por cuenta bancaria" color="#4ade80"/>
            {bancosLoad ? (
              <div style={{ color:"#4b5563", fontSize:13, textAlign:"center", padding:16 }}>
                <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight:6 }}/>
                Cargando…
              </div>
            ) : bancosList.length === 0 ? (
              <div style={{ color:"#4b5563", fontSize:13, textAlign:"center", padding:16 }}>
                No hay cuentas bancarias registradas
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
                {bancosList.map((b, i) => {
                  const saldo = Number(b.saldo ?? 0);
                  const maxS  = Math.max(...bancosList.map(x=>Number(x.saldo??0)), 1);
                  const pct   = Math.min((saldo / maxS) * 100, 100);
                  return (
                    <div key={b.id} style={{ padding:"10px 0",
                      borderBottom: i < bancosList.length-1 ? "1px solid #1e293b" : "none" }}>
                      <div style={{ display:"flex", justifyContent:"space-between",
                        alignItems:"center", marginBottom:6 }}>
                        <div>
                          <span style={{ fontSize:"0.85rem", fontWeight:600,
                            color:"#e5ecf5" }}>{b.banco}</span>
                          <span style={{ fontSize:"0.72rem", color:"#6b7280",
                            marginLeft:8, fontFamily:"monospace" }}>{b.numero_cuenta}</span>
                        </div>
                        <span style={{ fontFamily:"monospace", fontWeight:700,
                          fontSize:"0.88rem",
                          color: saldo > 0 ? "#4ade80" : "#6b7280" }}>
                          {fmt(saldo)}
                        </span>
                      </div>
                      <div style={{ height:4, background:"#374151", borderRadius:2 }}>
                        <div style={{ height:"100%", width:`${pct}%`, borderRadius:2,
                          background:"linear-gradient(90deg, #4ade80, #22c55e)",
                          transition:"width 0.8s ease" }}/>
                      </div>
                    </div>
                  );
                })}
                <div style={{ display:"flex", justifyContent:"flex-end",
                  marginTop:10, paddingTop:10, borderTop:"1px solid #364250" }}>
                  <span style={{ fontSize:"0.78rem", color:"#6b7280" }}>Total: </span>
                  <span style={{ fontFamily:"monospace", fontWeight:800,
                    color:"#4ade80", marginLeft:6, fontSize:"0.88rem" }}>
                    {fmt(totalBanco)}
                  </span>
                </div>
              </div>
            )}
          </Panel>

          {/* CxC vs CxP */}
          <Panel>
            <SectionTitle icon="fa-scale-balanced" label="Balance CxC vs CxP"/>
            {(cxcLoad || cxpLoad) ? (
              <div style={{ color:"#4b5563", fontSize:13, textAlign:"center", padding:16 }}>
                <i className="fa-solid fa-circle-notch fa-spin" style={{ marginRight:6 }}/>
                Cargando…
              </div>
            ) : (
              <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                <BarCompare
                  labelA="Por Cobrar" valA={totalCxC}
                  labelB="Por Pagar"  valB={totalCxP}
                  colorA="#f87171"    colorB="#fb923c"
                />
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                  {[
                    { label:"Total CxC", val:totalCxC, icon:"fa-arrow-down",
                      color:"#f87171", to:"/finanzas/cxc",
                      sub:`${cxcList.length} filiales` },
                    { label:"Total CxP", val:totalCxP, icon:"fa-arrow-up",
                      color:"#fb923c", to:"/finanzas/cxp",
                      sub:`${cxpList.length} facturas` },
                  ].map(({ label, val, icon, color, to, sub }) => (
                    <Link key={label} to={to} style={{ textDecoration:"none" }}>
                      <div style={{ background:"#111827", borderRadius:8,
                        padding:"12px 14px", border:"1px solid #364250",
                        transition:"border-color 0.15s" }}
                        onMouseOver={e => e.currentTarget.style.borderColor=color}
                        onMouseOut={e => e.currentTarget.style.borderColor="#364250"}>
                        <div style={{ display:"flex", alignItems:"center", gap:6,
                          marginBottom:6 }}>
                          <i className={`fa-solid ${icon}`} style={{ color, fontSize:"0.75rem" }}/>
                          <span style={{ fontSize:"0.72rem", color:"#6b7280",
                            fontWeight:600 }}>{label}</span>
                        </div>
                        <div style={{ fontFamily:"monospace", fontWeight:800,
                          fontSize:"1.1rem", color }}>
                          {fmtK(val)}
                        </div>
                        <div style={{ fontSize:"0.7rem", color:"#4b5563", marginTop:2 }}>
                          {sub}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </Panel>

          {/* Últimas facturas CxC */}
          <Panel>
            <div style={{ display:"flex", justifyContent:"space-between",
              alignItems:"center", marginBottom:14 }}>
              <SectionTitle icon="fa-file-invoice" label="Últimas facturas emitidas"
                color="#60a5fa"/>
              <Link to="/finanzas/facturas"
                style={{ fontSize:"0.75rem", color:"#f97316", fontWeight:600,
                  textDecoration:"none" }}>
                Ver todas →
              </Link>
            </div>
            {factList.length === 0 ? (
              <div style={{ color:"#4b5563", fontSize:13, textAlign:"center", padding:12 }}>
                No hay facturas recientes
              </div>
            ) : factList.map((f, i) => (
              <MiniRow key={f.id_factura ?? i}
                icon="fa-file-invoice"
                label={f.concepto ?? f.nombre_propietario ?? "—"}
                sub={`${f.nombre_propietario ?? ""} · ${fmtDate(f.fecha_emision)}`}
                value={fmt(f.monto)}
                color="#60a5fa"
              />
            ))}
          </Panel>

        </div>

        {/* ── RIGHT column ── */}
        <div style={{ display:"flex", flexDirection:"column", gap:16 }}>

          {/* Actividad reciente — asientos */}
          <Panel>
            <div style={{ display:"flex", justifyContent:"space-between",
              alignItems:"center", marginBottom:14 }}>
              <SectionTitle icon="fa-book-open" label="Asientos recientes" color="#a78bfa"/>
              <Link to="/finanzas/asientos"
                style={{ fontSize:"0.75rem", color:"#f97316", fontWeight:600,
                  textDecoration:"none" }}>
                Ver →
              </Link>
            </div>
            {asienList.length === 0 ? (
              <div style={{ color:"#4b5563", fontSize:12, textAlign:"center", padding:10 }}>
                Sin asientos recientes
              </div>
            ) : asienList.map((a, i) => {
              const typeColors = {
                FACTURA: "#60a5fa", RECIBO: "#4ade80", CXP: "#fb923c",
                MANUAL: "#a78bfa", INGRESO: "#4ade80", GASTO: "#f87171",
              };
              const col = typeColors[a.tipo] ?? "#9ca3af";
              return (
                <div key={a.id} style={{ padding:"9px 0",
                  borderBottom: i<asienList.length-1 ? "1px solid #1e293b" : "none" }}>
                  <div style={{ display:"flex", justifyContent:"space-between",
                    alignItems:"flex-start", gap:8 }}>
                    <div style={{ minWidth:0 }}>
                      <div style={{ fontSize:"0.78rem", color:"#e5ecf5", fontWeight:600,
                        overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                        {a.concepto}
                      </div>
                      <div style={{ display:"flex", alignItems:"center", gap:6, marginTop:3 }}>
                        <span style={{ fontSize:"0.65rem", fontWeight:700, padding:"1px 6px",
                          borderRadius:4, background:`${col}18`, color:col }}>
                          {a.tipo}
                        </span>
                        <span style={{ fontSize:"0.68rem", color:"#6b7280" }}>
                          {fmtDate(a.fecha)}
                        </span>
                      </div>
                    </div>
                    <span style={{ fontFamily:"monospace", fontWeight:700,
                      fontSize:"0.78rem", color: col, flexShrink:0 }}>
                      {fmtK(a.total_debitos ?? 0)}
                    </span>
                  </div>
                </div>
              );
            })}
          </Panel>

          {/* Últimos recibos */}
          <Panel>
            <div style={{ display:"flex", justifyContent:"space-between",
              alignItems:"center", marginBottom:14 }}>
              <SectionTitle icon="fa-receipt" label="Últimos recibos" color="#4ade80"/>
              <Link to="/finanzas/recibos"
                style={{ fontSize:"0.75rem", color:"#f97316", fontWeight:600,
                  textDecoration:"none" }}>
                Ver →
              </Link>
            </div>
            {reciList.length === 0 ? (
              <div style={{ color:"#4b5563", fontSize:12, textAlign:"center", padding:10 }}>
                Sin recibos recientes
              </div>
            ) : reciList.slice(0,4).map((r, i) => (
              <MiniRow key={r.numero_recibo ?? i}
                icon="fa-receipt"
                label={r.nombre_propietario ?? r.numero_recibo ?? "—"}
                sub={`${r.numero_recibo} · ${fmtDate(r.fecha_pago)}`}
                value={fmt(r.monto)}
                color="#4ade80"
              />
            ))}
          </Panel>

          {/* Accesos rápidos */}
          <Panel>
            <SectionTitle icon="fa-bolt" label="Accesos rápidos" color="#fbbf24"/>
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              <QuickLink to="/residentes"              icon="fa-users"                  label="Residentes"             color="#f97316"/>
              <QuickLink to="/finanzas/facturas/agregar" icon="fa-file-invoice"         label="Nueva Factura CxC"      color="#60a5fa"/>
              <QuickLink to="/finanzas/facturas/masivo" icon="fa-layer-group"           label="Facturación Masiva"     color="#a78bfa"/>
              <QuickLink to="/finanzas/cxp/facturas/agregar" icon="fa-file-invoice-dollar" label="Nueva Factura CxP" color="#fb923c"/>
              <QuickLink to="/finanzas/ingresos"       icon="fa-arrow-trend-up"         label="Registro de Ingresos"   color="#4ade80"/>
              <QuickLink to="/finanzas/gastos"         icon="fa-arrow-trend-down"       label="Registro de Gastos"     color="#f87171"/>
              <QuickLink to="/finanzas/estados?tab=balance-comprobacion" icon="fa-scale-balanced" label="Balance de Comprobación" color="#fbbf24"/>
            </div>
          </Panel>

        </div>
      </div>
    </div>
  );
}
